/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { FacetFilter } from 'festo-react-ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { useTranslation } from '../../hooks/useTranslation';
import FacetWrapper from './FacetWrapper';
import { fetchMedias, updateMediasInput, updateMediasSelected } from '../media/MediaSlice';
import { fetchMaterials, updateMaterialsInput, updateMaterialsSelected } from '../material/MaterialSlice';

import { OptionsType } from '../../components/filterTypes/multiselectbox/types';
import {
  fetchMaterialCategories,
  updateMaterialCategoriesInput,
  updateMaterialCategoriesSelected,
} from '../materialCategory/MaterialCategorySlice';
import { fetchResistanceTypes, updateResistanceTypesSelected } from '../resistanceType/ResistanceTypeSlice';
import { buildQuery } from '../../components/QueryBuilder';
import { addFilters, FilterSlice } from './filterSlice';
import { isMobile } from '../../helper';

const onChangeType: any = {
  medias: updateMediasSelected,
  materials: updateMaterialsSelected,
  materialCategories: updateMaterialCategoriesSelected,
  resistanceTypes: updateResistanceTypesSelected,
};

const onInputChangeType: any = {
  medias: updateMediasInput,
  materials: updateMaterialsInput,
  materialCategories: updateMaterialCategoriesInput,
  resistanceTypes: null,
};

interface FilterListProps {
  language: string;
  tableRef: React.RefObject<HTMLDivElement>;
}

const FilterList = React.forwardRef<HTMLDivElement, FilterListProps>(({ language, tableRef }) => {
  const t = useTranslation();
  const medias = useSelector((state: RootState) => state.medias);
  const materialCategories = useSelector((state: RootState) => state.materialCategories);
  const materials = useSelector((state: RootState) => state.materials);
  const resistanceTypes = useSelector((state: RootState) => state.resistanceTypes);
  const filterList = [medias, materialCategories, materials, resistanceTypes];
  const dispatch = useDispatch();
  const [isMaterialCategoryChange, setIsMaterialCategoryChange] = useState<boolean>(false);

  useEffect(() => {
    const query = buildQuery({ language, searchTerm: medias.inputValue });
    dispatch(fetchMedias(query));
  }, [dispatch, language, medias.inputValue]);

  useEffect(() => {
    const query = buildQuery({ language });
    dispatch(fetchMaterialCategories(query));
  }, [dispatch, language, materialCategories.inputValue]);

  useEffect(() => {
    setIsMaterialCategoryChange(true);
  }, [materialCategories.selectedIds]);

  useEffect(() => {
    if (materialCategories.values.length !== 0) {
      const query = buildQuery({
        language,
        name: materials.inputValue,
        materialCategoryIds: materialCategories.selectedIds,
      });
      dispatch(fetchMaterials(query, isMaterialCategoryChange));
      setIsMaterialCategoryChange(false);
    }
  }, [
    materials.inputValue,
    materialCategories.selectedIds,
    materialCategories.values,
    language,
    dispatch,
    isMaterialCategoryChange,
  ]);

  useEffect(() => {
    const media = [...medias.selected];
    const material = [...materials.selected];
    const materialCategory = [...materialCategories.selected];
    const resistance = [...resistanceTypes.selected];

    const resistanceTypeValue = resistanceTypes.values.map((item) => item.resistance);
    const resistanceTypeValueTK = resistanceTypes.values.map((item) => item.translationKey);

    const dictionary = {
      [resistanceTypeValue[0]]: resistanceTypeValueTK[0],
      [resistanceTypeValue[1]]: resistanceTypeValueTK[1],
      [resistanceTypeValue[2]]: resistanceTypeValueTK[2],
      [resistanceTypeValue[3]]: resistanceTypeValueTK[3],
    };

    const convertedResistance = resistance.map((item) => dictionary[item]);

    const selectedFilters: FilterSlice = {
      mediaFilter: media,
      materialFilter: material,
      materialCategoryFilter: materialCategory,
      filterResistanceType: convertedResistance,
    };

    dispatch(addFilters(selectedFilters));
  }, [
    medias.selected,
    materials.selected,
    materialCategories.selected,
    resistanceTypes.selected,
    dispatch,
    resistanceTypes.values,
  ]);

  useEffect(() => {
    const query = buildQuery({ language });
    dispatch(fetchResistanceTypes(query));
  }, [dispatch, language]);

  const gotoTableSectionRef = () => {
    if (tableRef && tableRef.current && isMobile(window)) {
      const y = tableRef.current.getBoundingClientRect().top + window.pageYOffset + -200;
      setTimeout(() => window.scrollTo({ top: y, behavior: 'smooth' }), 0);
    }
  };

  return (
    <FacetFilter title={t('filter')} buttonText={t('showResults')} onClick={gotoTableSectionRef}>
      <>
        {filterList.map((filter, index) => {
          const onInputChange = (inputValue: string) => {
            dispatch(onInputChangeType[filter.key](inputValue));
          };
          const onChange = (value: OptionsType) => {
            dispatch(onChangeType[filter.key](value));
          };
          return (
            <div className={filterList.length - 1 === index ? '' : 'pb-5'} key={filter.key}>
              <FacetWrapper
                econdaName={filter.econdaName}
                filterKey={filter.key}
                options={filter.values}
                label={t(filter.label)}
                onChange={onChange}
                onInputChange={onInputChange}
                isCombobox={filter.isCombobox}
                type={filter.type}
                selected={filter.selected}
                placeholder={t(filter.placeholder)}
                infoButtonDescription={t(filter.infoButtonDescription)}
                tableRef={tableRef}
              />
            </div>
          );
        })}
      </>
    </FacetFilter>
  );
});

export default FilterList;
